import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as api from "../api";
import { useToast } from "../components/Toast";
import Modal, { ModalActions } from "../components/Modal";
import { PlusIcon, XIcon, EditIcon, LockIcon } from "../components/Icons";

const PROJECT_TYPES = ["Ongoing", "Completed"];

// Display labels for project status (DB stays Ongoing/Completed; UI shows
// Under Construction / Operational per product naming).
const STATUS_LABELS = { Ongoing: "Under Construction", Completed: "Operational" };
const displayStatus = (s) => STATUS_LABELS[s] || s || "";
// Pseudo "category" used as a third option in the variable templates toggle —
// shows project-specific variables (those not backed by a template).
const PROJECT_SPECIFIC_KEY = "ProjectSpecific";
const TEMPLATE_TOGGLE_KEYS = [...PROJECT_TYPES, PROJECT_SPECIFIC_KEY];

// Fallback category list shown before the user has any custom categories.
// Real category list comes from /api/variable-categories (user-managed, ordered).
const FALLBACK_CATEGORIES = [
  "Profit & Loss Statement",
  "Cash Flow Statement",
  "Loan Schedule (Secured + Unsecured)",
  "Capital Structure",
  "Unit Economics",
];

const SCOPE_TABS = [
  { key: "project", label: "Project" },
  { key: "company", label: "Company" },
];

const FormulaIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" opacity="0.85">
    <path d="M7 2h10l-2 6h4l-9 14 3-10H7z" />
  </svg>
);

export default function SettingsPage({ canEdit = true }) {
  const [activeTab, setActiveTab] = useState("company-map");

  // ─── General settings ───
  const [settings, setSettings] = useState({});

  // ─── Variable formulas (keyed by variable name) ───
  const [formulas, setFormulas] = useState([]);
  const [presets, setPresets] = useState({});

  // ─── Mappings data ───
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [templates, setTemplates] = useState(null);
  // ─── Verticals (business lines) ───
  const [verticals, setVerticals] = useState([]);
  const [showAddVertical, setShowAddVertical] = useState(false);
  const [newVertical, setNewVertical] = useState({ name: "", code: "" });
  // ─── Variable categories (user-managed, ordered) ───
  const [categories, setCategories] = useState([]);
  // Add-category modal state
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  // Inline rename for an existing category
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");
  // Drag state for category reordering
  const [dragCategoryId, setDragCategoryId] = useState(null);
  // Collapsed by default — power users use this rarely once the order is set.
  const [categoriesCollapsed, setCategoriesCollapsed] = useState(true);
  // Project|Company sub-tab inside Variable Templates (shared across categories)
  const [scopeTab, setScopeTab] = useState("project");

  const [loading, setLoading] = useState(true);
  const toast = useToast();

  // ── Variable formula modal ──
  const [showFormulaModal, setShowFormulaModal] = useState(false);
  const [editingFormula, setEditingFormula] = useState(null);
  const [formulaForm, setFormulaForm] = useState({
    variable_name: "", formula_type: "growth", formula_expression: "", description: "",
  });

  // ── Add / Edit company modal ──
  // editingCompany: when non-null, the modal is in Edit mode for that company
  // (PUT instead of POST). Cleared when the modal closes.
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  // Header section collapsed by default — most users only set it once.
  const [headerCollapsed, setHeaderCollapsed] = useState(true);
  const [newCompany, setNewCompany] = useState({
    name: "", code: "",
    main_company_id: "", parent_company_id: "",
    parent_same_as_main: true,
    ownership_pct: "", vertical_id: "", is_proposed: false,
  });
  // ── Add project modal (used by Project ↔ Company mapping tab) ──
  const [showAddProject, setShowAddProject] = useState(false);
  const [newProject, setNewProject] = useState({
    name: "", status: "Ongoing", actual_year: 2025,
    vertical_id: "", parent_company_id: "", company_id: "", ownership_pct: "",
  });
  const [addingProject, setAddingProject] = useState(false);

  // ── Add / edit variable template modal ──
  const [showAddVar, setShowAddVar] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  // Inline editing for a project-specific Variable (no template backing).
  // Holds { id, name, category, description } of the row currently in edit mode.
  const [editingProjectVar, setEditingProjectVar] = useState(null);
  const [newVar, setNewVar] = useState({
    name: "", project_types: ["Ongoing"], var_type: "mandatory",
    category: "Profit & Loss Statement", description: "", propagate: true,
    is_subtotal: false, scope: "project", formula_expression: "",
  });
  const [affectedProjects, setAffectedProjects] = useState({ count: 0, projects: [] });
  const [addingVar, setAddingVar] = useState(false);
  const [varCategory, setVarCategory] = useState("Ongoing");

  const load = useCallback(() => {
    Promise.all([
      api.getSettings(),
      api.getFormulas(),
      // Settings only needs basic project fields — skip the P&L synthesis
      // pipeline (saves seconds when there are many projects in the DB).
      api.getProjects({ light: true }),
      api.getCompanies(),
      api.getProjectCompanyMappings(),
      api.getVariableTemplates(),
      api.getVariableCategories().catch(() => []),
      api.getVerticals().catch(() => []),
    ])
      .then(([s, f, pj, co, mp, tpl, cats, vrs]) => {
        setSettings(s);
        setFormulas(f.formulas || []);
        setPresets(f.presets || {});
        setProjects(pj);
        setCompanies(co);
        setMappings(mp);
        setTemplates(tpl);
        setCategories(Array.isArray(cats) ? cats : []);
        setVerticals(Array.isArray(vrs) ? vrs : []);
      })
      .catch(() => toast("Failed to load settings", "error"))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (showAddVar) {
      // Show affected count for the first selected type (informational only)
      const firstType = (newVar.project_types || [])[0] || "Ongoing";
      api.getAffectedProjects(firstType)
        .then(setAffectedProjects)
        .catch(() => setAffectedProjects({ count: 0, projects: [] }));
    }
  }, [showAddVar, newVar.project_types]);

  // Look up formula by variable name (case-sensitive match against template name)
  const formulaByVar = useMemo(() => {
    const m = {};
    formulas.forEach((f) => { m[f.variable_name] = f; });
    return m;
  }, [formulas]);

  // Dynamic category names in user-defined order. Falls back to the
  // hardcoded list before the first /api/variable-categories load.
  const VARIABLE_CATEGORIES = useMemo(() => {
    if (!categories || categories.length === 0) return FALLBACK_CATEGORIES;
    return categories.map((c) => c.name);
  }, [categories]);

  // ════════════════════════════════════════════
  // GENERAL
  // ════════════════════════════════════════════
  const handleSaveSettings = async () => {
    try { await api.updateSettings(settings); toast("Settings saved"); }
    catch (e) { toast(e.message, "error"); }
  };

  // ════════════════════════════════════════════
  // VARIABLE FORMULA
  // ════════════════════════════════════════════
  const openFormulaForVariable = (variableName) => {
    const existing = formulaByVar[variableName];
    if (existing) {
      setEditingFormula(existing);
      setFormulaForm({
        variable_name: existing.variable_name,
        formula_type: existing.formula_type,
        formula_expression: existing.formula_expression,
        description: existing.description || "",
      });
    } else {
      setEditingFormula(null);
      const defaultPreset = presets.growth;
      setFormulaForm({
        variable_name: variableName,
        formula_type: "growth",
        formula_expression: defaultPreset?.expression || "BUDGETED * Math.pow(1 + GROWTH_RATE, YEAR_INDEX)",
        description: defaultPreset?.description || "",
      });
    }
    setShowFormulaModal(true);
  };

  const handlePresetChange = (type) => {
    const p = presets[type];
    setFormulaForm((prev) => ({
      ...prev,
      formula_type: type,
      formula_expression: p?.expression || "",
      description: prev.description || p?.description || "",
    }));
  };

  const handleSaveFormula = async () => {
    if (!formulaForm.variable_name.trim()) return;
    try {
      if (editingFormula) {
        await api.updateFormula(editingFormula.id, formulaForm);
        toast("Formula updated");
      } else {
        await api.createFormula(formulaForm);
        toast("Formula saved");
      }
      setShowFormulaModal(false);
      load();
    } catch (e) { toast(e.message, "error"); }
  };

  const handleDeleteFormula = async (id) => {
    try { await api.deleteFormula(id); toast("Formula deleted"); load(); }
    catch (e) { toast(e.message, "error"); }
  };

  const testVarFormula = (expr) => {
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function("ACTUAL", "BUDGETED", "YEAR_INDEX", "GROWTH_RATE", "PREV", "return " + expr);
      const actual = 100, budgeted = 110, gr = 0.1;
      const results = []; let prev = budgeted;
      for (let i = 1; i <= 4; i++) { const val = fn(actual, budgeted, i, gr, prev); results.push(Math.round(val * 100) / 100); prev = val; }
      return results;
    } catch { return null; }
  };

  // ════════════════════════════════════════════
  // MAPPING — Project ↔ Company
  // ════════════════════════════════════════════
  const handleMappingChange = async (projectId, companyId) => {
    try { await api.updateProjectCompanyMapping(projectId, companyId); toast("Mapping updated"); load(); }
    catch (e) { toast(e.message, "error"); }
  };

  const handleAddCompany = async () => {
    const name = (newCompany.name || "").trim();
    if (!name) return;
    // Single source of truth — parent_company_id. main_company_id is
    // mirrored to keep legacy code paths happy until that column is
    // fully retired.
    const parentId = newCompany.parent_company_id || null;
    const payload = {
      name,
      code: (newCompany.code || "").trim(),
      main_company_id: parentId,
      parent_company_id: parentId,
      ownership_pct: newCompany.ownership_pct === "" ? null : parseFloat(newCompany.ownership_pct),
      vertical_id: newCompany.vertical_id || null,
      is_proposed: !!newCompany.is_proposed,
    };
    try {
      if (editingCompany) {
        await api.updateCompany(editingCompany.id, payload);
        toast(`Company "${name}" updated`);
      } else {
        await api.createCompany(payload);
        toast(`Company "${name}" added`);
      }
      setShowAddCompany(false);
      setEditingCompany(null);
      setNewCompany({
        name: "", code: "",
        main_company_id: "", parent_company_id: "",
        parent_same_as_main: true,
        ownership_pct: "", vertical_id: "", is_proposed: false,
      });
      load();
    } catch (e) { toast(e.message, "error"); }
  };

  // Companies eligible to appear in the Main / Parent dropdowns. Show
  // EVERY active company (not just root-level ones) so users can pick any
  // existing entity as a Main — keeps the dropdown contents consistent
  // with the Project Mapping table's Parent / Child Company selectors.
  // The only exclusion is self when editing (a company can't be its own
  // Main / Parent — that would create a cycle).
  const companyDropdownOptions = companies.filter(
    (c) => !editingCompany || c.id !== editingCompany.id
  );
  const mainCompanyOptions = companyDropdownOptions;

  // Label for the "no Main" option — uses the banner name from Settings
  // when configured so users see "📌 Ambuja Neotia Holdings…" instead of
  // a generic "None". Picking this places the company directly under the
  // top banner in the organigram.
  const bannerLabel = (settings.header_company_name || "").trim();
  const noMainOption = bannerLabel
    ? `🏛️  ${bannerLabel}  (banner / root)`
    : "— None (this is a Main Company) —";

  const getMappedCompany = (projectId) => mappings.find((x) => x.project_id === projectId)?.company_id || "";

  const handleAddProject = async () => {
    const name = (newProject.name || "").trim();
    if (!name) return;
    if (!newProject.company_id) {
      toast("Please pick a Child Company (its Vertical will be inherited)", "error");
      return;
    }
    setAddingProject(true);
    try {
      // Vertical is auto-derived on the backend from the company's
      // vertical_id; we still pass it as a hint when known.
      const co = companies.find((c) => c.id === newProject.company_id);
      const payload = {
        name,
        status: newProject.status,
        actual_year: parseInt(newProject.actual_year, 10) || 2025,
        company_id: newProject.company_id,
        vertical_id: (co && co.vertical_id) || null,
      };
      await api.createProject(payload);
      toast(`Project "${name}" created`);
      setShowAddProject(false);
      load();
    } catch (e) { toast(e.message, "error"); }
    setAddingProject(false);
  };

  // ════════════════════════════════════════════
  // MAPPING — Variable Templates
  // ════════════════════════════════════════════
  const openAddVarModal = (varType, presetCategory) => {
    setEditingTemplate(null);
    setVarRefSearch("");
    // The Project Specific tab is a view, not a project type — default to both.
    const initial = PROJECT_TYPES.includes(varCategory)
      ? [varCategory]
      : ["Ongoing", "Completed"];
    // Pre-pick the category from (1) the explicit preset, (2) the active
    // category tab if it's one of the canonical categories, else fall back
    // to P&L. This means clicking "Add Mandatory" while you're inside the
    // Loan Schedule tab pre-fills Loan Schedule.
    const cat = (presetCategory && VARIABLE_CATEGORIES.includes(presetCategory))
      ? presetCategory
      : (VARIABLE_CATEGORIES.includes(activeTplCategory)
          ? activeTplCategory
          : "Profit & Loss Statement");
    setNewVar({
      name: "", project_types: initial, var_type: varType,
      category: cat, description: "", propagate: true,
      is_subtotal: false, scope: scopeTab || "project", formula_expression: "",
    });
    setShowAddVar(true);
  };

  const openEditTemplateModal = (t) => {
    setEditingTemplate(t);
    setVarRefSearch("");
    // Pre-fill the existing formula for this variable name (if any) so the
    // user can edit it inline alongside other template fields.
    const existingFormula = formulas.find((f) => f.variable_name === t.name);
    // Determine which project categories already have a same-named template
    // (so the checkbox for an existing sibling shows as checked + disabled).
    const coveredTypes = [];
    PROJECT_TYPES.forEach((pt) => {
      const list = [
        ...(templates?.[pt]?.mandatory || []),
        ...(templates?.[pt]?.additional || []),
      ];
      if (list.some((x) => x.name === t.name)) coveredTypes.push(pt);
    });
    setNewVar({
      name: t.name,
      project_types: coveredTypes.length ? coveredTypes : [t.project_type || varCategory],
      var_type: t._var_type || "mandatory",
      category: VARIABLE_CATEGORIES.includes(t.category) ? t.category : VARIABLE_CATEGORIES[0],
      description: t.description || "",
      propagate: false,
      is_subtotal: !!t.subtotal,
      scope: t.scope || "project",
      formula_expression: existingFormula?.formula_expression || "",
      _existing_formula_id: existingFormula?.id,
      _existing_project_types: coveredTypes,    // categories that already have a sibling
    });
    setShowAddVar(true);
  };

  const handleSaveVariable = async () => {
    if (!newVar.name.trim()) return;
    const selectedTypes = (newVar.project_types || []).filter((t) => PROJECT_TYPES.includes(t));
    if (!editingTemplate && selectedTypes.length === 0) {
      toast("Pick at least one Project Statement", "error");
      return;
    }
    setAddingVar(true);
    try {
      if (editingTemplate) {
        await api.updateVariableTemplate(editingTemplate.id, {
          name: newVar.name.trim(),
          category: newVar.category.trim() || "General",
          description: newVar.description.trim(),
          var_type: newVar.var_type,
          is_subtotal: !!newVar.is_subtotal,
          scope: newVar.scope || "project",
        });

        // Create sibling templates for any newly-ticked Project Categories
        // that don't already have one (i.e. the user wants to extend this
        // variable into the other category in the same edit).
        const existing = newVar._existing_project_types || [];
        const newSiblings = (newVar.project_types || [])
          .filter((pt) => PROJECT_TYPES.includes(pt) && !existing.includes(pt));
        let siblingCount = 0;
        for (const project_type of newSiblings) {
          try {
            await api.addVariableTemplate({
              project_type, var_type: newVar.var_type,
              name: newVar.name.trim(),
              category: newVar.category.trim() || "General",
              description: newVar.description.trim(),
              propagate: true,
              is_subtotal: !!newVar.is_subtotal,
              scope: newVar.scope || "project",
            });
            siblingCount += 1;
          } catch (err) {
            toast(`Sibling for ${project_type} failed: ${err.message}`, "error");
          }
        }
        if (siblingCount > 0) {
          toast(`Variable "${newVar.name}" updated and added to ${newSiblings.join(" + ")} projects`);
        } else {
          toast(`Variable "${newVar.name}" updated`);
        }
      } else {
        // POST one template per selected project_type — backend stores them
        // independently so they remain editable per category.
        let totalPropagated = 0;
        for (const project_type of selectedTypes) {
          const result = await api.addVariableTemplate({
            project_type, var_type: newVar.var_type,
            name: newVar.name.trim(), category: newVar.category.trim() || "General",
            description: newVar.description.trim(),
            propagate: newVar.var_type === "mandatory" ? true : newVar.propagate,
            is_subtotal: !!newVar.is_subtotal,
            scope: newVar.scope || "project",
          });
          totalPropagated += result.propagated_count || 0;
        }
        const scope = selectedTypes.join(" + ");
        let msg = `Variable "${newVar.name}" added for ${scope} projects`;
        if (totalPropagated > 0) msg += ` — propagated to ${totalPropagated} project(s)`;
        toast(msg);
        setVarCategory(selectedTypes[0]);
      }

      // Optional formula — saves to FormulaConfig keyed by variable name.
      const expr = (newVar.formula_expression || "").trim();
      const existingId = newVar._existing_formula_id;
      if (expr) {
        try {
          if (existingId) {
            await api.updateFormula(existingId, {
              variable_name: newVar.name.trim(),
              formula_type: "custom",
              formula_expression: expr,
              description: newVar.description.trim(),
            });
          } else {
            await api.createFormula({
              variable_name: newVar.name.trim(),
              formula_type: "custom",
              formula_expression: expr,
              description: newVar.description.trim(),
            });
          }
        } catch (e) {
          toast("Variable saved but formula failed: " + e.message, "error");
        }
      } else if (existingId) {
        // User cleared the formula → remove it.
        try { await api.deleteFormula(existingId); } catch {}
      }

      setShowAddVar(false);
      setEditingTemplate(null);
      load();
    } catch (e) { toast(e.message, "error"); }
    setAddingVar(false);
  };

  const handleDeleteTemplate = async (id) => {
    try { await api.deleteVariableTemplate(id); toast("Template removed"); load(); }
    catch (e) { toast(e.message, "error"); }
  };

  // ── Active category tab in the templates list (was collapsible, now tabs).
  const [activeTplCategory, setActiveTplCategory] = useState(null);
  // Free-text filter — when non-empty, the category tabs collapse into a
  // single search-results view (across all categories).
  const [templateSearch, setTemplateSearch] = useState("");
  // Mandatory vs Additional sub-toggle (most variables are mandatory, so a
  // single full-width view per type is more useful than a 50/50 split).
  const [varTypeTab, setVarTypeTab] = useState("mandatory");
  // Search filter for the "Reference another variable" chip list inside the
  // formula editor of the Add/Edit Variable Template modal.
  const [varRefSearch, setVarRefSearch] = useState("");

  // ── Drag-to-sort for variable templates ──
  const [dragId, setDragId] = useState(null);
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const reorderWithin = async (groupItems, fromId, toId) => {
    if (!fromId || fromId === toId) return;
    const from = groupItems.findIndex((it) => it.id === fromId);
    const to = groupItems.findIndex((it) => it.id === toId);
    if (from < 0 || to < 0) return;
    // Move dragged item to the dropped-on position.
    const next = groupItems.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const items = next.map((it, idx) => ({ id: it.id, sort_order: idx + 1 }));
    try {
      await api.reorderVariableTemplates(items);
      load();
    } catch (e) { toast(e.message, "error"); }
  };

  // ── Project-specific Variable edit / delete ──
  const handleSaveProjectVar = async () => {
    if (!editingProjectVar) return;
    if (!editingProjectVar.name.trim()) return;
    try {
      await api.updateVariable(editingProjectVar.id, {
        name: editingProjectVar.name.trim(),
        category: editingProjectVar.category,
        description: editingProjectVar.description || "",
      });
      toast("Variable updated");
      setEditingProjectVar(null);
      load();
    } catch (e) { toast(e.message, "error"); }
  };
  const handleDeleteProjectVar = async (vid) => {
    try {
      await api.deleteVariable(vid);
      toast("Variable removed");
      load();
    } catch (e) { toast(e.message, "error"); }
  };

  // Group templates by category
  const groupByCategory = (list) => {
    const groups = {};
    (list || []).forEach((v) => { const c = v.category || "General"; if (!groups[c]) groups[c] = []; groups[c].push(v); });
    return groups;
  };

  const renderTemplateList = (list, varType, color) => {
    // Filter by the current Project|Company scope sub-tab.
    const scopedList = (list || []).filter(
      (v) => (v.scope || "project") === scopeTab
    );
    const groups = groupByCategory(scopedList);
    // Use the user-defined category order; unknown/legacy categories get
    // appended (alphabetical) at the end.
    const knownOrder = VARIABLE_CATEGORIES;
    const presentNames = Object.keys(groups);
    const catNames = [
      ...knownOrder.filter((n) => groups[n]),
      ...presentNames
        .filter((n) => !knownOrder.includes(n))
        .sort((a, b) => a === "General" ? 1 : b === "General" ? -1 : a.localeCompare(b)),
    ];
    list = scopedList;
    const q = (templateSearch || "").trim().toLowerCase();
    const isSearching = q.length > 0;

    // Default the active category to the first one with content.
    const firstCat = catNames[0] || null;
    const currentCat = (activeTplCategory && groups[activeTplCategory])
      ? activeTplCategory
      : firstCat;

    // Items to render: search-flat across categories, or just the active tab.
    const searchHits = isSearching
      ? list.filter((v) =>
          (v.name || "").toLowerCase().includes(q)
          || (v.description || "").toLowerCase().includes(q)
          || (v.category || "").toLowerCase().includes(q))
      : [];
    const itemsToRender = isSearching ? searchHits : (groups[currentCat] || []);
    const dropContext = isSearching ? itemsToRender : (groups[currentCat] || []);

    const renderRow = (v) => {
      const hasFormula = !!formulaByVar[v.name];
      const beingDragged = dragId === v.id;
      const isSubtotal = !!v.subtotal;
      return (
        <div key={v.id} className="flex justify-between items-center"
          draggable={canEdit && !isSearching}
          onDragStart={(e) => { setDragId(v.id); e.dataTransfer.effectAllowed = "move"; }}
          onDragEnd={() => setDragId(null)}
          onDragOver={onDragOver}
          onDrop={(e) => { e.preventDefault(); reorderWithin(dropContext, dragId, v.id); setDragId(null); }}
          title={canEdit && !isSearching ? "Drag to reorder" : undefined}
          style={{
            padding: "10px 14px",
            background: beingDragged
              ? "rgba(124,143,245,0.14)"
              : isSubtotal ? "rgba(124,143,245,0.10)" : "rgba(255,255,255,0.025)",
            borderRadius: 8, marginBottom: 4,
            border: beingDragged
              ? "1px dashed rgba(124,143,245,0.55)"
              : isSubtotal ? "1px solid rgba(124,143,245,0.30)" : "1px solid var(--border)",
            cursor: canEdit && !isSearching ? "grab" : "default",
            opacity: beingDragged ? 0.7 : 1,
            fontWeight: isSubtotal ? 600 : 400,
          }}>
          {canEdit && !isSearching && (
            <span className="text-dim mono" style={{ marginRight: 8, userSelect: "none", cursor: "grab", fontSize: 14 }}>⋮⋮</span>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="flex items-center gap-8" style={{ flexWrap: "wrap" }}>
              <span className={`text-sm ${isSubtotal ? "fw-700" : "fw-500"}`}
                style={isSubtotal ? { color: "var(--accent)" } : undefined}>{v.name}</span>
              {isSearching && v.category && (
                <span className="text-xxs text-dim" style={{
                  padding: "1px 8px", borderRadius: 10,
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid var(--border)",
                }}>{v.category}</span>
              )}
              {isSubtotal && (
                <span className="text-xxs fw-600" style={{
                  padding: "2px 8px", borderRadius: 12,
                  background: "rgba(124,143,245,0.18)", color: "var(--accent)",
                  border: "1px solid rgba(124,143,245,0.40)",
                }}>Total</span>
              )}
              {hasFormula && (
                <span className="text-xxs fw-600" style={{
                  padding: "2px 8px", borderRadius: 12,
                  background: "rgba(124,143,245,0.14)", color: "var(--accent)",
                  border: "1px solid rgba(124,143,245,0.25)",
                }}>
                  <FormulaIcon /> formula
                </span>
              )}
            </div>
            {v.description && <p className="text-xxs text-dim mt-4">{v.description}</p>}
            {hasFormula && (
              <code className="mono text-xxs" style={{ display: "inline-block", marginTop: 4, color: "var(--text-secondary)", background: "rgba(0,0,0,0.2)", padding: "2px 6px", borderRadius: 4 }}>
                {formulaByVar[v.name].formula_expression}
              </code>
            )}
          </div>
          <div className="flex items-center gap-6" style={{ marginLeft: 12, flexShrink: 0 }}>
            <span className="text-xxs" style={{ color }}>{varType === "mandatory" ? "Required" : "Optional"}</span>
            {canEdit && (
              <button className="btn-icon" title="Edit variable details, formula, type"
                onClick={() => openEditTemplateModal({ ...v, _var_type: varType })} style={{ padding: 4 }}>
                <EditIcon />
              </button>
            )}
            {canEdit && (
              <button className="btn-icon" title="Delete variable template"
                onClick={() => handleDeleteTemplate(v.id)} style={{ padding: 4 }}>
                <XIcon />
              </button>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="flex flex-col gap-8">
        {/* Search box — when non-empty, collapses category tabs into flat results. */}
        <div className="flex items-center gap-10" style={{ flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
            <input className="input input-sm" type="text"
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              placeholder="Search variables by name, description, or statement…"
              style={{ width: "100%", paddingRight: templateSearch ? 32 : 12 }} />
            {templateSearch && (
              <button type="button" onClick={() => setTemplateSearch("")}
                title="Clear search"
                style={{
                  position: "absolute", right: 6, top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent", border: "none",
                  color: "var(--text-muted)", cursor: "pointer", padding: 4,
                }}>
                <XIcon />
              </button>
            )}
          </div>
          {isSearching && (
            <span className="text-xxs text-dim">
              {searchHits.length} match{searchHits.length === 1 ? "" : "es"} across all statements
            </span>
          )}
        </div>

        {/* Category tabs — hidden while searching. */}
        {!isSearching && catNames.length > 0 && (
          <div className="tabs" style={{ flexWrap: "wrap", gap: 4 }}>
            {catNames.map((cat) => (
              <button key={cat} type="button"
                className={`tab ${currentCat === cat ? "active" : ""}`}
                onClick={() => setActiveTplCategory(cat)}>
                {cat}
                <span className="text-xxs text-dim" style={{ marginLeft: 6 }}>
                  ({groups[cat].length})
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Variable rows */}
        <div>
          {(list || []).length === 0 && (
            <p className="text-xs text-dim" style={{ padding: 8 }}>No variables defined.</p>
          )}
          {(list || []).length > 0 && itemsToRender.length === 0 && (
            <p className="text-xs text-dim" style={{ padding: 12, textAlign: "center" }}>
              {isSearching
                ? <>No variables match "<strong>{templateSearch}</strong>".</>
                : "No variables in this statement."}
            </p>
          )}
          {itemsToRender.map(renderRow)}
        </div>
      </div>
    );
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  const ongoingCount = projects.filter((p) => p.status === "Ongoing").length;
  const completedCount = projects.filter((p) => p.status === "Completed").length;
  const projectSpecific = templates?.ProjectSpecific || {};
  const projectSpecificCount = Object.values(projectSpecific).reduce(
    (n, p) => n + (p.variables?.length || 0), 0
  );
  const countsByType = {
    Ongoing: ongoingCount,
    Completed: completedCount,
    [PROJECT_SPECIFIC_KEY]: projectSpecificCount,
  };
  const tabLabel = (k) => k === PROJECT_SPECIFIC_KEY ? "Project Specific" : displayStatus(k);

  return (
    <div className="fade-in">
      <div className="tabs mb-24">
        <button className={`tab ${activeTab === "company-map" ? "active" : ""}`} onClick={() => setActiveTab("company-map")}>Project ↔ Company</button>
        <button className={`tab ${activeTab === "var-templates" ? "active" : ""}`} onClick={() => setActiveTab("var-templates")}>Variable Templates</button>
        <button className={`tab ${activeTab === "general" ? "active" : ""}`} onClick={() => setActiveTab("general")}>General</button>
      </div>

      {/* ═══ GENERAL ═══ */}
      {activeTab === "general" && (
        <div className="flex flex-col gap-20" style={{ maxWidth: 600 }}>
          <div className="card">
            <h3 className="heading fw-600 mb-20" style={{ fontSize: 17 }}>Application Settings</h3>
            <div className="flex flex-col gap-20">
              <div className="form-group"><label className="form-label">Projection Horizon (Years)</label>
                <input className="input" type="number" value={settings.projection_horizon || 5}
                  onChange={(e) => setSettings({ ...settings, projection_horizon: e.target.value })} style={{ maxWidth: 220 }} /></div>
              <div className="form-group"><label className="form-label">Currency</label>
                <select className="input select" value={settings.currency || "INR"} onChange={(e) => setSettings({ ...settings, currency: e.target.value })} style={{ maxWidth: 220 }}>
                  <option value="INR">₹ INR (Crores)</option><option value="USD">$ USD (Millions)</option>
                </select></div>
              <button className="btn btn-primary" style={{ width: "fit-content" }} onClick={handleSaveSettings}>Save Settings</button>
            </div>
          </div>

          <HistoryRetentionCard canEdit={canEdit} />
        </div>
      )}

      {/* ═══ PROJECT ↔ COMPANY / VERTICAL — drives the Organigram ═══ */}
      {activeTab === "company-map" && (
        <div className="card">
          <div className="flex justify-between items-center mb-16" style={{ flexWrap: "wrap", gap: 10 }}>
            <div className="flex items-center gap-8">
              <h3 className="heading fw-600" style={{ fontSize: 17 }}>Project ↔ Company / Vertical Mapping</h3>
              <span className="info-tip"
                title="The mapping below seeds the Group Organigram. Set the Main Company at the top, then for each project pick its Parent Company, Child Company, ownership %, vertical, status and Actual FY. Hit Sync Organigram to (re)build the chart from this data."
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 18, height: 18, borderRadius: "50%",
                  border: "1px solid var(--border-input)",
                  background: "rgba(124,143,245,0.08)", color: "var(--text-muted)",
                  fontSize: 11, cursor: "help", fontWeight: 700,
                  fontFamily: "'Georgia', 'Times New Roman', serif", fontStyle: "italic",
                }}>i</span>
            </div>
            <div className="flex gap-8" style={{ flexWrap: "wrap" }}>
              {canEdit && <button className="btn btn-ghost" onClick={() => { setShowAddVertical(true); setNewVertical({ name: "", code: "" }); }}><PlusIcon /> Add Vertical</button>}
              {canEdit && <button className="btn btn-ghost" onClick={() => setShowAddCompany(true)}><PlusIcon /> Add Company</button>}
              {canEdit && <button className="btn btn-ghost" onClick={() => {
                setNewProject({
                  name: "", status: "Ongoing", actual_year: 2025,
                  vertical_id: verticals[0]?.id || "",
                  parent_company_id: "", company_id: "", ownership_pct: "",
                });
                setShowAddProject(true);
              }}><PlusIcon /> Add Project</button>}
              {canEdit && (
                <button className="btn btn-primary"
                  onClick={async () => {
                    try {
                      await api.updateSettings({
                        header_company_name: settings.header_company_name || "",
                        header_subtitle: settings.header_subtitle || "",
                      });
                      const res = await api.syncOrganigramFromMapping();
                      toast(`Organigram synced (${res.nodes || 0} nodes)`);
                    } catch (e) { toast(e.message, "error"); }
                  }}>
                  Save &amp; Sync Organigram
                </button>
              )}
            </div>
          </div>

          {/* Organigram banner / root settings — collapsed by default.
              Most users only set the Title + Subtitle once at setup. */}
          <div className="sub-card mb-16" style={{ padding: 0 }}>
            <button type="button"
              onClick={() => setHeaderCollapsed((p) => !p)}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8,
                background: "transparent", border: "none", cursor: "pointer",
                padding: "10px 14px", color: "inherit", textAlign: "left",
              }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                style={{ transform: headerCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s", color: "var(--text-secondary)" }}>
                <path d="M6 9l6 6 6-6" />
              </svg>
              <span className="fw-600" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--text-secondary)" }}>
                Organigram Header
              </span>
              <span className="text-xxs text-dim" style={{ marginLeft: "auto" }}
                title="The banner shown at the top of the Group Organigram chart.">
                {(settings.header_company_name || "").trim() || "(not set)"}
              </span>
            </button>
            {!headerCollapsed && (
              <div className="flex flex-col gap-10" style={{ padding: "0 14px 14px" }}>
                <div className="grid-2 gap-12">
                  <div>
                    <label className="form-label">Header — Title</label>
                    <input className="input"
                      placeholder="e.g. Ambuja Neotia Holdings Pvt. Ltd. & Govind Commercial Co. Ltd."
                      disabled={!canEdit}
                      value={settings.header_company_name || ""}
                      onChange={(e) => setSettings({ ...settings, header_company_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="form-label">Header — Subtitle <span className="text-dim">(italics)</span></label>
                    <input className="input"
                      placeholder="e.g. Neotia Family"
                      disabled={!canEdit}
                      value={settings.header_subtitle || ""}
                      onChange={(e) => setSettings({ ...settings, header_subtitle: e.target.value })} />
                  </div>
                </div>
                {canEdit && (
                  <div className="flex gap-8" style={{ flexWrap: "wrap" }}>
                    <button className="btn btn-secondary btn-sm" onClick={handleSaveSettings}>Save Header</button>
                    <button className="btn btn-ghost btn-sm"
                      title="One-shot sample data — adds Neotia-group companies and projects if not already present"
                      onClick={async () => {
                        if (!window.confirm("Add sample companies + projects (Neotia group) to your DB? Skips anything that already exists by name.")) return;
                        try {
                          const res = await api.loadOrganigramSample();
                          toast(`Sample loaded — ${res.companies_added || 0} companies, ${res.projects_added || 0} projects added`);
                          load();
                        } catch (e) { toast(e.message, "error"); }
                      }}>
                      Load Sample Data
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Project</th>
                <th>Status</th>
                <th title="Pipeline — Under-Construction projects only. When checked, this project's data rolls into the Pipeline column of all Reports (no FY allocation).">Pipeline</th>
                <th>Actual FY</th>
                <th>Vertical</th>
                <th>Parent Company</th>
                <th>Child Company</th>
                <th>%</th>
                <th>Code</th>
                <th>Notes</th>
              </tr></thead>
              <tbody>
                {projects.map((p) => {
                  const mappedId = getMappedCompany(p.id);
                  const company = companies.find((c) => c.id === mappedId);
                  // Parent + % are now properties of the Child Company —
                  // derived here for display, edited via the Edit Company
                  // modal (the ✎ pencil on each company card below).
                  const derivedParent = company && company.parent_company_id
                    ? companies.find((c) => c.id === company.parent_company_id)
                    : null;
                  const derivedPct = company ? company.ownership_pct : null;
                  // Vertical is also a Company property now — derived for
                  // display, edited via the company card.
                  const derivedVertical = company && company.vertical_id
                    ? verticals.find((v) => v.id === company.vertical_id)
                    : null;
                  return (
                    <tr key={p.id}>
                      <td className="fw-500">{p.name}</td>
                      <td>{canEdit ? (
                        <select className="input select" style={{ width: "auto" }}
                          value={p.status}
                          onChange={async (e) => {
                            try {
                              await api.updateProject(p.id, { status: e.target.value });
                              toast("Status updated");
                              load();
                            } catch (err) { toast(err.message, "error"); }
                          }}>
                          <option value="Ongoing">Under Construction</option>
                          <option value="Completed">Operational</option>
                        </select>
                      ) : (
                        <span className={`badge ${p.status === "Ongoing" ? "badge-ongoing" : "badge-completed"}`}>{displayStatus(p.status)}</span>
                      )}</td>
                      <td style={{ textAlign: "center" }}>
                        {p.status === "Ongoing" ? (
                          <input type="checkbox"
                            checked={!!p.is_pipeline}
                            disabled={!canEdit}
                            title="When checked, this project's data rolls into the Pipeline column of every Report (no FY allocation)."
                            onChange={async (e) => {
                              try {
                                await api.updateProject(p.id, { is_pipeline: e.target.checked });
                                toast(e.target.checked ? "Marked as Pipeline" : "Removed from Pipeline");
                                load();
                              } catch (err) { toast(err.message, "error"); }
                            }}
                            style={{ accentColor: "var(--accent)", width: 16, height: 16, cursor: canEdit ? "pointer" : "default" }} />
                        ) : (
                          <span className="text-xxs text-dim" title="Only Under-Construction projects can be flagged as Pipeline.">—</span>
                        )}
                      </td>
                      <td>{canEdit ? (
                        <div className="flex items-center gap-6" style={{ flexWrap: "wrap" }}>
                          <select className="input select" style={{ width: "auto" }}
                            value={p.actual_year || ""}
                            onChange={async (e) => {
                              const yr = parseInt(e.target.value);
                              if (!yr || yr === p.actual_year) return;
                              if (!window.confirm(`Set "${p.name}" Actual to FY ${yr}? Years before become Historical (preserved); the forward window seeds new rows where missing.`)) return;
                              try {
                                await api.changeActualYear(p.id, yr);
                                toast(`Actual year for "${p.name}" set to FY ${yr}`);
                                load();
                              } catch (err) { toast(err.message, "error"); }
                            }}>
                            {(() => {
                              const ay = p.actual_year || 2025;
                              const opts = [];
                              for (let i = -10; i <= 10; i += 1) opts.push(ay + i);
                              return opts.map((y) => <option key={y} value={y}>FY {y}</option>);
                            })()}
                          </select>
                          {p.previous_actual_year && p.previous_actual_year !== p.actual_year && (
                            <button type="button" className="btn-icon"
                              title={`Rollback to FY ${p.previous_actual_year}`}
                              onClick={async () => {
                                if (!window.confirm(`Rollback "${p.name}" Actual to FY ${p.previous_actual_year}? Year-wise data stays in place; this just relabels the columns.`)) return;
                                try {
                                  await api.changeActualYear(p.id, p.previous_actual_year);
                                  toast(`Reverted to FY ${p.previous_actual_year}`);
                                  load();
                                } catch (err) { toast(err.message, "error"); }
                              }}
                              style={{ padding: "2px 8px", color: "var(--orange-light)",
                                       border: "1px solid rgba(245,158,11,0.35)",
                                       borderRadius: 6, background: "rgba(245,158,11,0.10)",
                                       fontSize: 11, fontWeight: 600 }}>
                              ↶ FY {p.previous_actual_year}
                            </button>
                          )}
                        </div>
                      ) : <span className="mono">FY {p.actual_year}</span>}</td>
                      {/* Vertical is now a Company property — read-only,
                          derived from Child Company. Edit on the company card. */}
                      <td>
                        <span
                          title={`Vertical of "${company?.name || "this company"}". Edit via the ✎ pencil on the company card below.`}
                          style={{
                            color: derivedVertical ? "var(--text-secondary)" : "var(--text-muted)",
                            fontStyle: derivedVertical ? "normal" : "italic",
                          }}>
                          {derivedVertical ? derivedVertical.name : "—"}
                        </span>
                      </td>
                      {/* Parent Company is now derived (read-only) — the
                          source of truth is Company.parent_company_id,
                          edited via the ✎ pencil on the company card. */}
                      <td style={{ maxWidth: 180 }}>
                        <span style={{
                          display: "inline-block", maxWidth: 170,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          verticalAlign: "bottom",
                          color: derivedParent ? "var(--text-secondary)" : "var(--text-muted)",
                          fontStyle: derivedParent ? "normal" : "italic",
                        }}
                          title={derivedParent
                            ? `Parent of "${company?.name || "—"}". Edit via the ✎ pencil on the ${company?.name || ""} card below.`
                            : "Set Parent by editing the Child Company below."}>
                          {derivedParent ? derivedParent.name : (company ? "— (under banner)" : "—")}
                        </span>
                      </td>
                      <td>{canEdit ? (
                        <select className="input select" style={{ width: "auto" }} value={mappedId} onChange={(e) => handleMappingChange(p.id, e.target.value)}>
                          <option value="">— Select —</option>
                          {companies.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}{c.code ? ` (${c.code})` : ""}
                            </option>
                          ))}
                        </select>
                      ) : <span>{company?.name || "Unmapped"}</span>}</td>
                      {/* Ownership % — read-only, derived from
                          Company.ownership_pct. Edit via the company card. */}
                      <td>
                        <span className="mono"
                          title={`Ownership % of "${company?.name || "this company"}" by its Parent. Edit via the ✎ pencil on the company card below.`}
                          style={{ color: derivedPct != null ? "var(--text-secondary)" : "var(--text-muted)" }}>
                          {derivedPct != null ? `${derivedPct}%` : "—"}
                        </span>
                      </td>
                      <td className="mono text-accent">{company?.code || "—"}</td>
                      <td>{canEdit ? (
                        <input className="input input-sm"
                          style={{ minWidth: 180 }}
                          defaultValue={p.notes || ""}
                          placeholder="e.g. (14.29% Owned by KMC)"
                          onBlur={async (e) => {
                            const next = e.target.value.trim();
                            if ((p.notes || "") === next) return;
                            try {
                              await api.updateProject(p.id, { notes: next });
                              toast("Notes updated");
                              load();
                            } catch (err) { toast(err.message, "error"); }
                          }} />
                      ) : <span className="text-xxs text-dim">{p.notes || "—"}</span>}</td>
                    </tr>
                  );
                })}
                {projects.length === 0 && <tr><td colSpan={10} className="text-muted" style={{ textAlign: "center", padding: 24 }}>No projects yet.</td></tr>}
              </tbody>
            </table>
          </div>
          {verticals.length > 0 && (
            <>
              <h4 className="heading fw-600 mt-24 mb-12" style={{ fontSize: 14.5 }}>
                Registered Verticals
                {canEdit && <span className="text-xxs text-dim" style={{ marginLeft: 8, fontWeight: 400 }}>· click name or code to edit</span>}
              </h4>
              <div className="flex gap-12" style={{ flexWrap: "wrap" }}>
                {verticals.map((v) => (
                  <EditableVerticalCard key={v.id} vertical={v} canEdit={canEdit}
                    onSaved={load} toast={toast} />
                ))}
              </div>
            </>
          )}
          {companies.length > 0 && (
            <>
              <h4 className="heading fw-600 mt-24 mb-12" style={{ fontSize: 14.5 }}>
                Registered Companies
                {canEdit && <span className="text-xxs text-dim" style={{ marginLeft: 8, fontWeight: 400 }}>· click name or code to edit</span>}
              </h4>
              <div className="flex gap-12" style={{ flexWrap: "wrap" }}>
                {companies.map((c) => (
                  <EditableCompanyCard key={c.id} company={c} canEdit={canEdit}
                    onSaved={load} toast={toast}
                    onEditHierarchy={(comp) => {
                      // Prefill the Add Company modal in EDIT mode with this
                      // company's current hierarchy fields.
                      setEditingCompany(comp);
                      setNewCompany({
                        name: comp.name || "",
                        code: comp.code || "",
                        main_company_id: comp.main_company_id || "",
                        parent_company_id: comp.parent_company_id || "",
                        parent_same_as_main: (comp.parent_company_id || "") === (comp.main_company_id || ""),
                        ownership_pct: comp.ownership_pct ?? "",
                        vertical_id: comp.vertical_id || "",
                        is_proposed: !!comp.is_proposed,
                      });
                      setShowAddCompany(true);
                    }} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══ VARIABLE TEMPLATES ═══ */}
      {activeTab === "var-templates" && templates && (
        <div className="card">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-8">
              <h3 className="heading fw-600" style={{ fontSize: 17 }}>Variable Templates</h3>
              <span className="info-tip"
                title="Variables auto-propagate to matching projects. Add a projection formula on any variable to control how its projected years are calculated."
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 18, height: 18, borderRadius: "50%",
                  border: "1px solid var(--border-input)",
                  background: "rgba(124,143,245,0.08)", color: "var(--text-muted)",
                  fontSize: 11, cursor: "help", fontWeight: 700,
                  fontFamily: "'Georgia', 'Times New Roman', serif", fontStyle: "italic",
                }}>i</span>
            </div>
            {canEdit && <button className="btn btn-primary" onClick={() => openAddVarModal("mandatory")}><PlusIcon /> Add Variable</button>}
          </div>

          {/* ── Statements management (collapsible) ── */}
          <div className="sub-card mt-16 mb-16" style={{ padding: 14 }}>
            <div className="flex justify-between items-center" style={{ flexWrap: "wrap", gap: 8,
                  marginBottom: categoriesCollapsed ? 0 : 8 }}>
              <button type="button"
                onClick={() => setCategoriesCollapsed((p) => !p)}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: "transparent", border: "none", cursor: "pointer",
                  textAlign: "left", padding: 0, color: "inherit", flex: 1, minWidth: 0,
                }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                  style={{ transform: categoriesCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s ease", flexShrink: 0, color: "var(--text-secondary)" }}>
                  <path d="M6 9l6 6 6-6" />
                </svg>
                <div>
                  <h4 className="fw-600" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--text-secondary)" }}>
                    Statements
                    <span className="text-xxs text-dim" style={{ marginLeft: 8, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                      ({categories.length})
                    </span>
                    <span className="info-tip"
                      title="Drag to reorder. The order here drives the tab sequence in Project Detail and Company Detail."
                      style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 16, height: 16, borderRadius: "50%",
                        border: "1px solid var(--border-input)",
                        background: "rgba(124,143,245,0.08)", color: "var(--text-muted)",
                        fontSize: 10, cursor: "help", marginLeft: 8, fontWeight: 700,
                        fontFamily: "'Georgia', 'Times New Roman', serif", fontStyle: "italic",
                      }}>i</span>
                  </h4>
                </div>
              </button>
              {canEdit && !categoriesCollapsed && (
                <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); setShowAddCategory(true); setNewCategoryName(""); }}>
                  <PlusIcon /> Add Statement
                </button>
              )}
            </div>
            {!categoriesCollapsed && (
            <div className="flex flex-col gap-4">
              {categories.length === 0 && (
                <p className="text-xxs text-dim" style={{ padding: 6 }}>
                  No custom statements yet — using the default list.
                </p>
              )}
              {categories.map((c, idx) => {
                const beingDragged = dragCategoryId === c.id;
                const isEditing = editingCategoryId === c.id;
                return (
                  <div key={c.id}
                    draggable={canEdit && !isEditing}
                    onDragStart={(e) => { setDragCategoryId(c.id); e.dataTransfer.effectAllowed = "move"; }}
                    onDragEnd={() => setDragCategoryId(null)}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      const fromId = dragCategoryId;
                      const toId = c.id;
                      setDragCategoryId(null);
                      if (!fromId || fromId === toId) return;
                      const from = categories.findIndex((x) => x.id === fromId);
                      const to = categories.findIndex((x) => x.id === toId);
                      if (from < 0 || to < 0) return;
                      const next = categories.slice();
                      const [moved] = next.splice(from, 1);
                      next.splice(to, 0, moved);
                      setCategories(next);  // optimistic
                      try {
                        await api.reorderVariableCategories(
                          next.map((x, i) => ({ id: x.id, sort_order: i + 1 }))
                        );
                        load();
                      } catch (err) { toast(err.message, "error"); load(); }
                    }}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 12px",
                      background: beingDragged ? "rgba(124,143,245,0.14)" : "rgba(255,255,255,0.025)",
                      border: beingDragged ? "1px dashed rgba(124,143,245,0.55)" : "1px solid var(--border)",
                      borderRadius: 8,
                      cursor: canEdit && !isEditing ? "grab" : "default",
                      opacity: beingDragged ? 0.7 : 1,
                    }}>
                    {canEdit && !isEditing && (
                      <span className="text-dim mono" style={{ userSelect: "none", cursor: "grab", fontSize: 14 }}>⋮⋮</span>
                    )}
                    <span className="text-xxs text-dim mono" style={{ minWidth: 24 }}>#{idx + 1}</span>
                    {isEditing ? (
                      <>
                        <input className="input input-sm" autoFocus
                          value={editingCategoryName}
                          onChange={(e) => setEditingCategoryName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              (async () => {
                                try {
                                  await api.updateVariableCategory(c.id, { name: editingCategoryName.trim() });
                                  toast("Statement renamed");
                                  setEditingCategoryId(null);
                                  load();
                                } catch (err) { toast(err.message, "error"); }
                              })();
                            }
                            if (e.key === "Escape") { e.preventDefault(); setEditingCategoryId(null); }
                          }}
                          style={{ flex: 1, padding: "4px 8px" }} />
                        <button className="btn-icon" onClick={() => setEditingCategoryId(null)}><XIcon /></button>
                      </>
                    ) : (
                      <>
                        <span className="text-sm fw-500" style={{ flex: 1 }}>{c.name}</span>
                        {canEdit && (
                          <>
                            <button className="btn-icon" title="Rename"
                              onClick={() => { setEditingCategoryId(c.id); setEditingCategoryName(c.name); }}>
                              <EditIcon />
                            </button>
                            <button className="btn-icon" title="Delete statement"
                              onClick={async () => {
                                try {
                                  await api.deleteVariableCategory(c.id);
                                  toast("Statement deleted");
                                  load();
                                } catch (err) { toast(err.message, "error"); }
                              }}>
                              <XIcon />
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </div>

          <div className="flex items-center gap-16 mb-20 mt-16" style={{ flexWrap: "wrap" }}>
            <span className="text-xs text-muted fw-600" style={{ textTransform: "uppercase", letterSpacing: 0.8 }}>View:</span>
            <div className="tabs" style={{ padding: 3 }}>
              {TEMPLATE_TOGGLE_KEYS.map((cat) => (
                <button key={cat} className={`tab ${varCategory === cat ? "active" : ""}`}
                  onClick={() => setVarCategory(cat)}>
                  {tabLabel(cat)} <span className="text-xxs text-muted" style={{ marginLeft: 6 }}>({countsByType[cat] || 0})</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Project Specific tab ── */}
          {varCategory === PROJECT_SPECIFIC_KEY ? (
            <div>
              <p className="text-xs text-muted mb-16">
                Variables not backed by any active template — typically created via project → Add Variable. They live only in their owning project.
                Promote one to a global template by re-adding it via the <strong>+ Add Variable</strong> button above with the desired Project Statement.
              </p>
              {Object.keys(projectSpecific).length === 0 && (
                <div className="info-box">No project-specific variables. Every variable is backed by a template.</div>
              )}
              <div className="flex flex-col gap-16">
                {Object.entries(projectSpecific).map(([pid, info]) => (
                  <div key={pid} style={{ background: "rgba(255,255,255,0.02)", borderRadius: 12, padding: 18, border: "1px solid var(--border)" }}>
                    <div className="flex items-center gap-10 mb-12" style={{ flexWrap: "wrap" }}>
                      <h4 className="fw-600" style={{ fontSize: 14.5, color: "var(--text-primary)" }}>{info.project_name}</h4>
                      <span className={`badge ${info.project_status === "Ongoing" ? "badge-ongoing" : "badge-completed"}`}>{displayStatus(info.project_status)}</span>
                      <span className="text-xxs text-dim">({info.variables.length} local variable{info.variables.length === 1 ? "" : "s"})</span>
                    </div>
                    <div className="flex flex-col gap-4">
                      {info.variables.map((v) => {
                        const isEditing = editingProjectVar?.id === v.id;
                        if (isEditing) {
                          return (
                            <div key={v.id}
                              style={{ padding: "10px 12px", background: "rgba(124,143,245,0.06)", borderRadius: 8, border: "1px solid rgba(124,143,245,0.35)" }}>
                              <div className="flex gap-8 mb-8" style={{ flexWrap: "wrap" }}>
                                <input className="input input-sm" style={{ flex: "1 1 200px", textAlign: "left" }}
                                  placeholder="Variable name" autoFocus
                                  value={editingProjectVar.name}
                                  onChange={(e) => setEditingProjectVar({ ...editingProjectVar, name: e.target.value })} />
                                <select className="input select"
                                  style={{ width: "auto", padding: "6px 30px 6px 10px", fontSize: 12 }}
                                  value={editingProjectVar.category}
                                  onChange={(e) => setEditingProjectVar({ ...editingProjectVar, category: e.target.value })}>
                                  {VARIABLE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </div>
                              <input className="input input-sm" style={{ textAlign: "left" }}
                                placeholder="Description (optional)"
                                value={editingProjectVar.description || ""}
                                onChange={(e) => setEditingProjectVar({ ...editingProjectVar, description: e.target.value })} />
                              <div className="flex justify-end gap-6 mt-8">
                                <button className="btn btn-ghost btn-sm" onClick={() => setEditingProjectVar(null)}>Cancel</button>
                                <button className="btn btn-primary btn-sm"
                                  disabled={!editingProjectVar.name.trim()}
                                  onClick={handleSaveProjectVar}>Save</button>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={v.id} className="flex justify-between items-center"
                            style={{ padding: "8px 12px", background: "rgba(255,255,255,0.02)", borderRadius: 8, border: "1px solid var(--border)" }}>
                            <div style={{ minWidth: 0 }}>
                              <div className="text-sm fw-500">{v.name}</div>
                              {v.description && <p className="text-xxs text-dim mt-2">{v.description}</p>}
                            </div>
                            <div className="flex items-center gap-8" style={{ flexShrink: 0 }}>
                              <span className="text-xxs text-muted">{v.category}</span>
                              {v.mandatory && <span className="badge-required">Required</span>}
                              {canEdit && (
                                <>
                                  <button className="btn-icon" title="Edit variable"
                                    onClick={() => setEditingProjectVar({
                                      id: v.id,
                                      name: v.name,
                                      category: VARIABLE_CATEGORIES.includes(v.category) ? v.category : VARIABLE_CATEGORIES[0],
                                      description: v.description || "",
                                    })}>
                                    <EditIcon />
                                  </button>
                                  {/* Project-specific variables don't have a template
                                      backing them, so the mandatory flag is essentially
                                      meaningless — always allow delete here. */}
                                  <button className="btn-icon" title="Delete variable"
                                    onClick={() => {
                                      if (window.confirm(`Remove "${v.name}" from this project? Its values will be deactivated.`)) {
                                        handleDeleteProjectVar(v.id);
                                      }
                                    }}>
                                    <XIcon />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {(() => {
                const matching = projects.filter((p) => p.status === varCategory);
                if (matching.length === 0) return null;
                return (
                  <div className="info-box mb-20">
                    <strong>{displayStatus(varCategory)}</strong> variables apply to: {matching.map((p) => p.name).join(", ")}
                  </div>
                );
              })()}

              {(() => {
                const fullMandatory = templates[varCategory]?.mandatory || [];
                const fullAdditional = templates[varCategory]?.additional || [];
                // Counts in the sub-tab badges reflect the active scope only.
                const inScope = (arr) => arr.filter((v) => (v.scope || "project") === scopeTab);
                const mandatoryCount = inScope(fullMandatory).length;
                const additionalCount = inScope(fullAdditional).length;
                const isMandatory = varTypeTab === "mandatory";
                const list = isMandatory ? fullMandatory : fullAdditional;
                const accentColor = isMandatory ? "var(--orange-light)" : "var(--accent)";
                return (
                  <div style={{ background: "rgba(255,255,255,0.02)", borderRadius: 12, padding: 20, border: "1px solid var(--border)" }}>
                    {/* Scope toggle removed — all variables are now
                        project-level. scopeTab state stays pinned to
                        "project" so the filter below still works. */}
                    <div className="flex justify-between items-center mb-16" style={{ flexWrap: "wrap", gap: 10 }}>
                      <div className="tabs" style={{ padding: 3 }}>
                        <button type="button"
                          className={`tab ${isMandatory ? "active" : ""}`}
                          onClick={() => setVarTypeTab("mandatory")}>
                          Mandatory <span className="text-xxs text-muted" style={{ marginLeft: 6 }}>({mandatoryCount})</span>
                        </button>
                        <button type="button"
                          className={`tab ${!isMandatory ? "active" : ""}`}
                          onClick={() => setVarTypeTab("additional")}>
                          Additional <span className="text-xxs text-muted" style={{ marginLeft: 6 }}>({additionalCount})</span>
                        </button>
                      </div>
                      <p className="text-xs text-muted" style={{ flex: 1, minWidth: 200, textAlign: "right" }}>
                        {isMandatory
                          ? "Auto-populated and locked on all matching projects"
                          : "Auto-added but removable per project"}
                      </p>
                      {canEdit && (
                        <button className="btn btn-primary btn-sm"
                          title={`Add a new ${isMandatory ? "Mandatory" : "Additional"} variable${VARIABLE_CATEGORIES.includes(activeTplCategory) ? ` to ${activeTplCategory}` : ""}`}
                          onClick={() => openAddVarModal(varTypeTab)}>
                          <PlusIcon /> Add {isMandatory ? "Mandatory" : "Additional"}
                        </button>
                      )}
                    </div>
                    <div style={{ borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
                      {renderTemplateList(list, varTypeTab, accentColor)}
                    </div>
                    {canEdit && (
                      <button className="btn btn-ghost btn-sm mt-12"
                        onClick={() => openAddVarModal(varTypeTab)}>
                        <PlusIcon /> Add {isMandatory ? "Mandatory" : "Additional"}
                      </button>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* ═══ VARIABLE FORMULA MODAL ═══ */}
      {showFormulaModal && (
        <Modal title={editingFormula ? `Edit Formula — ${formulaForm.variable_name}` : `Add Formula — ${formulaForm.variable_name}`} onClose={() => setShowFormulaModal(false)}>
          {!editingFormula && (
            <p className="text-xs text-muted mb-16">Set how projected years are calculated for <strong>{formulaForm.variable_name}</strong>. The formula is keyed by variable name and applies wherever this variable exists.</p>
          )}
          <div className="form-group"><label className="form-label">Preset</label>
            <div className="flex gap-6" style={{ flexWrap: "wrap" }}>
              {Object.entries(presets).map(([k, p]) => (
                <button key={k} className={`btn btn-sm ${formulaForm.formula_type === k ? "btn-primary" : "btn-ghost"}`} onClick={() => handlePresetChange(k)}>{p.label}</button>
              ))}
              <button className={`btn btn-sm ${formulaForm.formula_type === "custom" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setFormulaForm({ ...formulaForm, formula_type: "custom" })}>Custom</button>
            </div>
          </div>

          <div style={{ background: "rgba(124,143,245,0.05)", border: "1px solid rgba(124,143,245,0.12)", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
            <p className="text-xxs fw-600 mb-6" style={{ color: "var(--accent)" }}>Available placeholders</p>
            <div className="flex gap-12" style={{ flexWrap: "wrap" }}>
              {[["ACTUAL", "Actual year value"], ["BUDGETED", "Budgeted year value"], ["YEAR_INDEX", "1, 2, 3..."], ["GROWTH_RATE", "(BUD-ACT)/ACT"], ["PREV", "Previous year"]].map(([n, d]) => (
                <span key={n} className="text-xxs"><code className="mono" style={{ color: "var(--accent)", background: "rgba(124,143,245,0.12)", padding: "1.5px 6px", borderRadius: 3 }}>{n}</code> {d}</span>
              ))}
            </div>
          </div>

          <div className="form-group"><label className="form-label">Expression</label>
            <input className="input mono" value={formulaForm.formula_expression}
              onChange={(e) => setFormulaForm({ ...formulaForm, formula_expression: e.target.value })}
              placeholder="BUDGETED * Math.pow(1 + GROWTH_RATE, YEAR_INDEX)" />
            {formulaForm.formula_expression && (() => {
              const p = testVarFormula(formulaForm.formula_expression);
              if (!p) return <p className="text-xxs mt-6" style={{ color: "var(--red-light)" }}>⚠ Invalid expression</p>;
              return <div className="flex gap-6 mt-6">{p.map((v, i) => <span key={i} className="mono text-xxs" style={{ color: "var(--green-light)" }}>Y{i + 1}: {v}</span>)}</div>;
            })()}
          </div>

          <div className="form-group"><label className="form-label">Description <span className="text-dim">(optional)</span></label>
            <input className="input" value={formulaForm.description}
              onChange={(e) => setFormulaForm({ ...formulaForm, description: e.target.value })} /></div>

          <ModalActions
            onCancel={() => setShowFormulaModal(false)}
            onConfirm={handleSaveFormula}
            confirmLabel={editingFormula ? "Update Formula" : "Save Formula"}
            disabled={!formulaForm.variable_name.trim() || !formulaForm.formula_expression.trim()} />
        </Modal>
      )}

      {/* ═══ ADD / EDIT COMPANY MODAL ═══ */}
      {showAddCompany && (
        <Modal
          title={editingCompany ? `Edit Company — ${editingCompany.name}` : "Add Company"}
          width="640px"
          onClose={() => { setShowAddCompany(false); setEditingCompany(null); }}>
          <div className="grid-2 gap-12">
            <div className="form-group"><label className="form-label">Company Name <span className="text-accent">*</span></label>
              <input className="input" autoFocus value={newCompany.name}
                onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                placeholder="e.g. Neotia Hospitality Pvt Ltd" /></div>
            <div className="form-group"><label className="form-label">Code</label>
              <input className="input" value={newCompany.code}
                onChange={(e) => setNewCompany({ ...newCompany, code: e.target.value })}
                placeholder="e.g. NHPL" /></div>
          </div>

          {/* Single "Parent Company" field — picks the direct parent in
              the organigram. Selecting the banner option places this
              company directly under the chart root. */}
          <div className="form-group">
            <label className="form-label">Parent Company</label>
            <select className="input select" value={newCompany.parent_company_id}
              onChange={(e) => setNewCompany({
                ...newCompany,
                parent_company_id: e.target.value,
                // Keep main_company_id mirrored so older sync code paths
                // still see a sensible value; not surfaced in the UI.
                main_company_id: e.target.value,
                parent_same_as_main: true,
              })}>
              <option value="">{noMainOption}</option>
              {companyDropdownOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.code ? ` (${c.code})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="grid-2 gap-12">
            <div className="form-group"><label className="form-label">Ownership % <span className="text-dim">(parent’s stake in this company)</span></label>
              <input className="input mono" type="number" step="0.01" min="0" max="100"
                value={newCompany.ownership_pct}
                onChange={(e) => setNewCompany({ ...newCompany, ownership_pct: e.target.value })}
                placeholder="e.g. 60" /></div>
            <div className="form-group"><label className="form-label">Vertical</label>
              <select className="input select" value={newCompany.vertical_id}
                onChange={(e) => setNewCompany({ ...newCompany, vertical_id: e.target.value })}>
                <option value="">— Select —</option>
                {verticals.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select></div>
          </div>

          <div className="form-group">
            <label className="flex items-center gap-10" style={{ cursor: "pointer",
              padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border-input)",
              background: newCompany.is_proposed ? "rgba(245,158,11,0.10)" : "rgba(255,255,255,0.03)",
            }}>
              <input type="checkbox" checked={!!newCompany.is_proposed}
                onChange={(e) => setNewCompany({ ...newCompany, is_proposed: e.target.checked })}
                style={{ accentColor: "var(--orange-light)", width: 16, height: 16 }} />
              <span className="text-sm">Proposed <span className="text-dim">(renders dashed connector from parent)</span></span>
            </label>
          </div>

          <ModalActions
            onCancel={() => { setShowAddCompany(false); setEditingCompany(null); }}
            onConfirm={handleAddCompany}
            confirmLabel={editingCompany ? "Save Changes" : "Add Company"}
            disabled={!newCompany.name.trim()} />
        </Modal>
      )}

      {/* ═══ ADD PROJECT MODAL ═══ */}
      {showAddProject && (
        <Modal title="Add Project" width="640px" onClose={() => setShowAddProject(false)}>
          <div className="form-group"><label className="form-label">Project Name</label>
            <input className="input" autoFocus value={newProject.name}
              onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
              placeholder="e.g. Patna Hotel & Mall" /></div>
          <div className="grid-2 gap-12">
            <div className="form-group"><label className="form-label">Status</label>
              <select className="input select" value={newProject.status}
                onChange={(e) => setNewProject({ ...newProject, status: e.target.value })}>
                <option value="Ongoing">Under Construction</option>
                <option value="Completed">Operational</option>
              </select></div>
            <div className="form-group"><label className="form-label">Actual FY</label>
              <input className="input mono" type="number"
                value={newProject.actual_year}
                onChange={(e) => setNewProject({ ...newProject, actual_year: e.target.value })} /></div>
          </div>
          {/* Child Company is the only company-side input on a project —
              the company carries its own Parent, %, and Vertical (set via
              Add/Edit Company), so we don't ask for those again here. */}
          <div className="form-group"><label className="form-label">Child Company <span className="text-accent">*</span></label>
            <select className="input select" value={newProject.company_id}
              onChange={(e) => setNewProject({ ...newProject, company_id: e.target.value })}>
              <option value="">— Select —</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.code ? ` (${c.code})` : ""}
                </option>
              ))}
            </select>
            {newProject.company_id && (() => {
              const co = companies.find((c) => c.id === newProject.company_id);
              const v = co && co.vertical_id ? verticals.find((x) => x.id === co.vertical_id) : null;
              return (
                <p className="text-xxs text-dim mt-4">
                  Vertical: <span style={{ color: v ? "var(--text-secondary)" : "var(--text-muted)" }}>
                    {v ? v.name : "— not set on this company —"}
                  </span>
                  {!v && " (edit the company to set its Vertical)"}
                </p>
              );
            })()}
          </div>
          <ModalActions onCancel={() => setShowAddProject(false)} onConfirm={handleAddProject}
            confirmLabel={addingProject ? "Adding..." : "Add Project"}
            disabled={!newProject.name.trim() || !newProject.company_id || addingProject} />
        </Modal>
      )}

      {showAddVertical && (
        <Modal title="Add Vertical" onClose={() => setShowAddVertical(false)}>
          <div className="form-group"><label className="form-label">Vertical Name</label>
            <input className="input" value={newVertical.name}
              onChange={(e) => setNewVertical({ ...newVertical, name: e.target.value })}
              placeholder="e.g. Hospitality, Real Estate, F&B" autoFocus /></div>
          <div className="form-group"><label className="form-label">Code <span className="text-dim">(optional)</span></label>
            <input className="input" value={newVertical.code}
              onChange={(e) => setNewVertical({ ...newVertical, code: e.target.value })}
              placeholder="e.g. HOSP" /></div>
          <ModalActions
            onCancel={() => setShowAddVertical(false)}
            onConfirm={async () => {
              const name = (newVertical.name || "").trim();
              if (!name) return;
              try {
                await api.createVertical({ name, code: (newVertical.code || "").trim() });
                toast(`Vertical "${name}" added`);
                setShowAddVertical(false);
                setNewVertical({ name: "", code: "" });
                load();
              } catch (e) { toast(e.message, "error"); }
            }}
            confirmLabel="Add Vertical"
            disabled={!(newVertical.name || "").trim()} />
        </Modal>
      )}

      {/* ═══ ADD CATEGORY MODAL ═══ */}
      {showAddCategory && (
        <Modal title="Add Statement" onClose={() => setShowAddCategory(false)}>
          <div className="form-group"><label className="form-label">Statement Name</label>
            <input className="input" value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="e.g. Working Capital, Tax & Compliance"
              autoFocus />
            <p className="text-xxs text-dim mt-4">
              The new statement appears in the Project Detail and Company Detail tab strips, in the order set above.
            </p>
          </div>
          <ModalActions
            onCancel={() => setShowAddCategory(false)}
            onConfirm={async () => {
              const name = newCategoryName.trim();
              if (!name) return;
              try {
                await api.addVariableCategory({ name });
                toast(`Statement "${name}" added`);
                setShowAddCategory(false);
                setNewCategoryName("");
                load();
              } catch (e) { toast(e.message, "error"); }
            }}
            confirmLabel="Add Statement"
            disabled={!newCategoryName.trim()} />
        </Modal>
      )}

      {/* ═══ ADD / EDIT VARIABLE TEMPLATE MODAL ═══ */}
      {showAddVar && (
        <Modal
          title={editingTemplate ? `Edit Variable — ${editingTemplate.name}` : "Add Variable Template"}
          width="900px"
          onClose={() => { setShowAddVar(false); setEditingTemplate(null); }}>

          <div className="form-group"><label className="form-label">Variable Name</label>
            <input className="input" value={newVar.name} onChange={(e) => setNewVar({ ...newVar, name: e.target.value })}
              placeholder="e.g. Room Revenue, Occupancy %, ARR" autoFocus /></div>

          <div className="form-group"><label className="form-label">Statement</label>
            <select className="input select" value={newVar.category}
              onChange={(e) => setNewVar({ ...newVar, category: e.target.value })}>
              {VARIABLE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <p className="text-xxs text-dim mt-4">Drives the tab where this variable shows up in each project.</p></div>

          <div className="form-group"><label className="form-label">Description <span className="text-dim">(optional)</span></label>
            <input className="input" value={newVar.description} onChange={(e) => setNewVar({ ...newVar, description: e.target.value })}
              placeholder="Shows as tooltip when hovering over variable name" /></div>

          <div className="form-group">
            <label className="form-label">
              Project Statement <span className="text-dim">(check one or both)</span>
            </label>
            <div className="flex gap-8">
              {PROJECT_TYPES.map((cat) => {
                const isExisting = (newVar._existing_project_types || []).includes(cat);
                const checked = (newVar.project_types || []).includes(cat);
                return (
                  <label key={cat} className="flex items-center gap-8"
                    style={{
                      flex: 1, padding: "10px 14px", borderRadius: 9,
                      cursor: isExisting ? "default" : "pointer",
                      background: checked ? "rgba(124,143,245,0.15)" : "rgba(255,255,255,0.03)",
                      border: checked ? "1px solid rgba(124,143,245,0.45)" : "1px solid var(--border-input)",
                      opacity: isExisting && !checked ? 0.7 : 1,
                    }}>
                    <input type="checkbox"
                      checked={checked}
                      disabled={isExisting}
                      onChange={(e) => {
                        const next = new Set(newVar.project_types || []);
                        if (e.target.checked) next.add(cat); else next.delete(cat);
                        setNewVar({ ...newVar, project_types: Array.from(next) });
                      }}
                      style={{ accentColor: "var(--accent)" }} />
                    <span className="text-sm fw-500">{displayStatus(cat)}</span>
                    {isExisting && (
                      <span className="text-xxs text-dim" style={{ marginLeft: "auto" }}>exists</span>
                    )}
                  </label>
                );
              })}
            </div>
            <p className="text-xxs text-dim mt-6">
              {editingTemplate
                ? "Existing templates are locked. Tick the unchecked one to create a sibling template that mirrors this variable in the other project category."
                : "Selecting both creates two templates (one Under Construction, one Operational) so the variable propagates to every matching project."}
            </p>
          </div>

          {!editingTemplate && newVar.var_type === "additional" && (
            <div className="form-group"><label className="flex items-center gap-10" style={{ cursor: "pointer" }}>
              <input type="checkbox" checked={newVar.propagate} onChange={(e) => setNewVar({ ...newVar, propagate: e.target.checked })}
                style={{ accentColor: "var(--accent)", width: 16, height: 16 }} />
              <span className="text-sm">Add to all existing matching projects</span>
            </label></div>
          )}

          {/* Variable Type — shown in BOTH add and edit modes so users can flip
              mandatory ↔ additional after the fact. Sibling templates and the
              Variable.mandatory flag on every matching project sync server-side. */}
          <div className="form-group"><label className="form-label">Variable Type</label>
            <div className="flex gap-8">
              <button type="button" className={`btn ${newVar.var_type === "mandatory" ? "btn-primary" : "btn-ghost"}`}
                style={{ flex: 1 }} onClick={() => setNewVar({ ...newVar, var_type: "mandatory", propagate: true })}><LockIcon /> Mandatory</button>
              <button type="button" className={`btn ${newVar.var_type === "additional" ? "btn-primary" : "btn-ghost"}`}
                style={{ flex: 1 }} onClick={() => setNewVar({ ...newVar, var_type: "additional" })}><EditIcon /> Additional</button>
            </div>
            <p className="text-xxs text-dim mt-6">
              {newVar.var_type === "mandatory"
                ? "Auto-added to all matching projects and locked from removal."
                : "Auto-added to all matching projects but can be removed per project."}
            </p>
          </div>

          {/* Scope removed — every template is project-scoped by default. */}

          {/* Subtotal / Total flag — drives bold + lavender tint and the
              "Summary only" filter on project tables. Replaces a previous
              name-pattern heuristic so users can mark any variable explicitly. */}
          <div className="form-group">
            <label className="flex items-center gap-10" style={{ cursor: "pointer",
              padding: "10px 14px", borderRadius: 9,
              background: newVar.is_subtotal ? "rgba(124,143,245,0.15)" : "rgba(255,255,255,0.03)",
              border: newVar.is_subtotal ? "1px solid rgba(124,143,245,0.45)" : "1px solid var(--border-input)",
            }}>
              <input type="checkbox" checked={!!newVar.is_subtotal}
                onChange={(e) => setNewVar({ ...newVar, is_subtotal: e.target.checked })}
                style={{ accentColor: "var(--accent)", width: 16, height: 16 }} />
              <span className="text-sm fw-500">Mark as Subtotal / Total row</span>
              {newVar.is_subtotal && (
                <span className="text-xxs fw-600" style={{
                  marginLeft: "auto",
                  padding: "2px 8px", borderRadius: 12,
                  background: "rgba(124,143,245,0.20)", color: "var(--accent)",
                  border: "1px solid rgba(124,143,245,0.40)",
                  letterSpacing: 0.4, textTransform: "uppercase",
                }}>Total</span>
              )}
            </label>
            <p className="text-xxs text-dim mt-6">
              Subtotal rows are rendered bold with a lavender tint and a "Total" chip in project tables, and can be filtered via the "Summary only" toggle.
            </p>
          </div>

          {editingTemplate && (
            <div className="info-box mb-16">
              <p className="text-xs">
                Editing variable scoped to <strong>{displayStatus(editingTemplate.project_type) || (newVar.project_types || []).map(displayStatus).join(" + ")}</strong> projects.
                Project statement can't be changed once created — delete and re-add to move it. Variable Type and other fields edit freely.
              </p>
            </div>
          )}

          {/* ── Optional Formula ── */}
          <div className="form-group">
            <label className="form-label">Projection Formula <span className="text-dim">(optional)</span></label>
            <div style={{ background: "rgba(124,143,245,0.05)", border: "1px solid rgba(124,143,245,0.18)", borderRadius: 10, padding: "10px 12px", marginBottom: 8 }}>
              <p className="text-xxs fw-600 mb-6" style={{ color: "var(--accent)" }}>Click to insert</p>
              <div className="flex gap-6 mb-8" style={{ flexWrap: "wrap" }}>
                {[
                  ["ACTUAL", "Actual year value"],
                  ["BUDGETED", "Budgeted year value"],
                  ["YEAR_INDEX", "1, 2, 3..."],
                  ["GROWTH_RATE", "(BUD-ACT)/ACT"],
                  ["PREV", "Previous year of THIS variable"],
                  ["YEAR", "FY-end year (2025, 2026...)"],
                  ["DAYS_IN_FY", "365 / 366 (leap-year aware)"],
                ].map(([n, d]) => (
                  <button key={n} type="button" title={d}
                    onClick={() => setNewVar((p) => ({
                      ...p,
                      formula_expression: (p.formula_expression || "") +
                        ((p.formula_expression || "") && !/[\s+\-*/(]$/.test(p.formula_expression) ? " " : "") + n,
                    }))}
                    style={{
                      cursor: "pointer", border: "1px solid rgba(124,143,245,0.35)",
                      background: "rgba(124,143,245,0.12)", color: "var(--accent)",
                      padding: "3px 9px", borderRadius: 6, fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 11.5, fontWeight: 500,
                    }}>{n}</button>
                ))}
                <span style={{ width: 1, background: "var(--border)", margin: "0 4px" }} />
                {/* Year-position booleans for mixed actual/projected logic. */}
                {[
                  ["IS_ACTUAL",    "true (1) only on the Actual year"],
                  ["IS_BUDGETED",  "true (1) only on the Budgeted year"],
                  ["IS_PROJECTED", "true (1) on Projected years (year ≥ Actual+2)"],
                ].map(([n, d]) => (
                  <button key={n} type="button" title={d}
                    onClick={() => setNewVar((p) => ({
                      ...p,
                      formula_expression: (p.formula_expression || "") +
                        ((p.formula_expression || "") && !/[\s+\-*/(]$/.test(p.formula_expression) ? " " : "") + n,
                    }))}
                    style={{
                      cursor: "pointer", border: "1px solid rgba(245,158,11,0.35)",
                      background: "rgba(245,158,11,0.10)", color: "var(--orange-light)",
                      padding: "3px 9px", borderRadius: 6, fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 11.5, fontWeight: 500,
                    }}>{n}</button>
                ))}
                <button type="button"
                  title="IF(condition, valueIfTrue, valueIfFalse) — spreadsheet-style conditional. Works in both frontend and backend formulas."
                  onClick={() => setNewVar((p) => ({
                    ...p,
                    formula_expression: (p.formula_expression || "") +
                      ((p.formula_expression || "") && !/[\s+\-*/(]$/.test(p.formula_expression) ? " " : "") + "IF(",
                  }))}
                  style={{
                    cursor: "pointer", border: "1px solid rgba(245,158,11,0.35)",
                    background: "rgba(245,158,11,0.10)", color: "var(--orange-light)",
                    padding: "3px 9px", borderRadius: 6, fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 11.5, fontWeight: 500,
                  }}>IF(</button>
                <span style={{ width: 1, background: "var(--border)", margin: "0 4px" }} />
                {["+", "-", "*", "/", "(", ")", ","].map((op) => (
                  <button key={op} type="button"
                    onClick={() => setNewVar((p) => ({ ...p, formula_expression: (p.formula_expression || "") + " " + op + " " }))}
                    style={{
                      cursor: "pointer", border: "1px solid var(--border-input)",
                      background: "rgba(255,255,255,0.05)", color: "var(--text-primary)",
                      padding: "3px 9px", borderRadius: 6, fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 12, minWidth: 28,
                    }}>{op}</button>
                ))}
              </div>
              {(() => {
                // Build referenceable variable names grouped by source so the
                // user can see at-a-glance where each variable comes from
                // (Input Data, Input → Capex / MoF / Budget, Variable Templates
                // by category, or system-synthesized P&L).
                const insertChip = (n, withPrev = false) => () => setNewVar((p) => ({
                  ...p,
                  formula_expression: (p.formula_expression || "") +
                    ((p.formula_expression || "") && !/[\s+\-*/(]$/.test(p.formula_expression) ? " " : "") +
                    (withPrev ? `{${n}@PREV}` : `{${n}}`),
                }));

                // Templates grouped by category, in user-defined category order.
                const tplCatBuckets = {};
                ["Ongoing", "Completed", "Both"].forEach((pt) => {
                  (templates?.[pt]?.mandatory || []).forEach((t) => {
                    const c = t.category || "General";
                    if (!tplCatBuckets[c]) tplCatBuckets[c] = new Set();
                    tplCatBuckets[c].add(t.name);
                  });
                  (templates?.[pt]?.additional || []).forEach((t) => {
                    const c = t.category || "General";
                    if (!tplCatBuckets[c]) tplCatBuckets[c] = new Set();
                    tplCatBuckets[c].add(t.name);
                  });
                });
                const orderedTplCats = [
                  ...VARIABLE_CATEGORIES.filter((c) => tplCatBuckets[c]),
                  ...Object.keys(tplCatBuckets)
                    .filter((c) => !VARIABLE_CATEGORIES.includes(c))
                    .sort((a, b) => a === "General" ? 1 : b === "General" ? -1 : a.localeCompare(b)),
                ];

                // Pre-defined groups for the in-app data sources.
                const groups = [];
                groups.push({
                  label: "Input Data",
                  hint: "Project-level assumption rows (Rate of Interest, growth %, ratios, ₹ Cr inputs).",
                  names: [
                    "Secured Loan Interest Rate", "Unsecured Loan Interest Rate",
                    "Occupancy Growth %", "ARR Growth %",
                    "F&B Revenue % of Room Revenue", "OOD Revenue % of Room Revenue",
                    "Operating Expenses", "Corporate Expenses",
                    "Depreciation", "Finance Cost", "Tax",
                  ],
                });
                groups.push({
                  label: "Input → Capex Year-wise",
                  hint: "Sum of Development + OH Contingency for each particular. Capex Total = sum across all line items.",
                  names: [
                    "Capex Total",
                    "Capex - Land", "Capex - Building", "Capex - Equipment",
                    "Capex - Plant", "Capex - Other",
                  ],
                });
                groups.push({
                  label: "Input → Means of Finance",
                  hint: "Per-project sums from the Means of Finance table.",
                  names: [
                    "Means of Finance - Capex",
                    "Means of Finance - Equity",
                    "Means of Finance - Unsecured Loan",
                    "Means of Finance - Bank Loan",
                    "Means of Finance - Total",
                  ],
                });
                groups.push({
                  label: "Input → Budget Summary",
                  hint: "Cells from the Budget Summary tab (manual or formula-driven).",
                  names: [
                    "Budget - Land", "Budget - Development",
                    "Budget - Project Overhead", "Budget - Contingency",
                    "Budget - Interest Cost",
                    "Budget - Equity", "Budget - Unsecured Loan", "Budget - Bank Loan",
                    "Budget - Total Cost", "Budget - Total MoF",
                  ],
                });
                orderedTplCats.forEach((cat) => {
                  groups.push({
                    label: `Variable Templates → ${cat}`,
                    hint: `Variables defined in Settings → Variable Templates under "${cat}".`,
                    names: Array.from(tplCatBuckets[cat]).sort(),
                  });
                });
                groups.push({
                  label: "System-synthesized P&L",
                  hint: "Auto-computed by the backend from sub-units, assumptions, and breakdowns.",
                  names: [
                    "Total Room Count", "Rooms Sold", "Occupancy %", "ARR",
                    "No. of Days of Operation", "Room Revenue", "F&B Revenue",
                    "OOD Revenue", "Total Revenue", "EBIDTA", "PBT", "PAT",
                    "Operating Expenses %",
                  ],
                });

                // Dedup: a name should only appear in the FIRST group that
                // claims it. Earlier groups (Input Data → Capex → MoF → Budget
                // → Templates → Synthesized) win.
                const seen = new Set();
                const dedupedGroups = groups.map((g) => {
                  const fresh = (g.names || []).filter((n) => {
                    if (!n || seen.has(n)) return false;
                    seen.add(n);
                    return true;
                  });
                  return { ...g, names: fresh };
                }).filter((g) => g.names.length > 0);

                const q = (varRefSearch || "").trim().toLowerCase();
                const filteredGroups = q
                  ? dedupedGroups.map((g) => ({
                      ...g,
                      names: g.names.filter((n) => n.toLowerCase().includes(q)),
                    })).filter((g) => g.names.length > 0)
                  : dedupedGroups;
                const totalCount = dedupedGroups.reduce((acc, g) => acc + g.names.length, 0);
                const matchCount = filteredGroups.reduce((acc, g) => acc + g.names.length, 0);

                return (
                  <>
                    <div className="flex justify-between items-center mb-6 mt-8" style={{ gap: 8, flexWrap: "wrap" }}>
                      <p className="text-xxs fw-600" style={{ color: "var(--green-light)" }}>
                        Reference another variable
                        <span className="text-xxs text-dim" style={{ marginLeft: 6, fontWeight: 400 }}>
                          ({matchCount}{q ? ` of ${totalCount}` : ""} across {filteredGroups.length} group{filteredGroups.length === 1 ? "" : "s"})
                        </span>
                      </p>
                      <input className="input input-sm" type="text"
                        placeholder="Search variables…"
                        value={varRefSearch}
                        onChange={(e) => setVarRefSearch(e.target.value)}
                        style={{ width: 220, textAlign: "left" }} />
                    </div>
                    <div style={{ maxHeight: 280, overflowY: "auto", paddingRight: 4 }}>
                      {filteredGroups.length === 0 && (
                        <p className="text-xxs text-dim" style={{ padding: "4px 0" }}>
                          No variables match "{varRefSearch}".
                        </p>
                      )}
                      {filteredGroups.map((g) => (
                        <div key={g.label} className="mb-10">
                          <div className="flex items-baseline gap-8 mb-4" style={{ flexWrap: "wrap" }}>
                            <span className="text-xxs fw-700" style={{
                              color: "var(--green-light)", letterSpacing: 0.5,
                              textTransform: "uppercase",
                            }}>{g.label}</span>
                            <span className="text-xxs text-dim">({g.names.length})</span>
                            {g.hint && <span className="text-xxs text-dim" style={{ flex: 1, minWidth: 200 }}>{g.hint}</span>}
                          </div>
                          <div className="flex gap-4" style={{ flexWrap: "wrap" }}>
                            {g.names.map((n) => (
                              <span key={n} className="flex items-center" style={{ gap: 0 }}>
                                <button type="button" title={`Insert {${n}} — same-year value`}
                                  onClick={insertChip(n, false)}
                                  style={{
                                    cursor: "pointer",
                                    border: "1px solid rgba(52,211,153,0.3)",
                                    borderRight: "none",
                                    borderRadius: "6px 0 0 6px",
                                    background: "rgba(52,211,153,0.08)", color: "var(--green-light)",
                                    padding: "2px 8px", fontSize: 11,
                                  }}>{n}</button>
                                <button type="button" title={`Insert {${n}@PREV} — previous-year value`}
                                  onClick={insertChip(n, true)}
                                  style={{
                                    cursor: "pointer",
                                    border: "1px solid rgba(52,211,153,0.3)",
                                    borderRadius: "0 6px 6px 0",
                                    background: "rgba(52,211,153,0.18)", color: "var(--green-light)",
                                    padding: "2px 6px", fontSize: 10, fontWeight: 600,
                                  }}>@PREV</button>
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xxs text-dim mt-6">
                      <code className="mono">{"{Var Name}"}</code> = same-year value &nbsp;·&nbsp;
                      <code className="mono">{"{Var Name@PREV}"}</code> = previous-year value of that variable.
                    </p>
                  </>
                );
              })()}
            </div>
            <input className="input mono" value={newVar.formula_expression || ""}
              onChange={(e) => setNewVar({ ...newVar, formula_expression: e.target.value })}
              placeholder="e.g. IF(IS_PROJECTED, PREV * (1 + {Occupancy Growth %}), {Rooms Sold} / {Total Room Count})" />
            <p className="text-xxs text-dim mt-4">
              Leave blank for default compound growth. <code>{"{Variable Name}"}</code> = same-year value, <code>{"{Variable Name@PREV}"}</code> = previous-year value.
              Use <code className="text-accent">IS_ACTUAL</code> / <code className="text-accent">IS_BUDGETED</code> / <code className="text-accent">IS_PROJECTED</code> with <code className="text-accent">IF(cond, a, b)</code> for mixed actual/projected logic.
              Capex line items are exposed as <code>{"{Capex - <Particulars>}"}</code> (e.g. <code>{"{Capex - Land}"}</code>) and <code>{"{Capex Total}"}</code> for the per-project sum.
            </p>
          </div>

          <ModalActions
            onCancel={() => { setShowAddVar(false); setEditingTemplate(null); }}
            onConfirm={handleSaveVariable}
            confirmLabel={addingVar ? (editingTemplate ? "Saving..." : "Adding...") : (editingTemplate ? "Save Changes" : "Add Variable")}
            disabled={!newVar.name.trim() || addingVar} />
        </Modal>
      )}
    </div>
  );
}


/* ─── History retention + purge (Admin) ─── */
function HistoryRetentionCard({ canEdit }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftMonths, setDraftMonths] = useState("");
  const [savingMonths, setSavingMonths] = useState(false);
  const [purgeMonths, setPurgeMonths] = useState("");
  const [purging, setPurging] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.getHistoryRetention();
      setData(r);
      setDraftMonths(String(r.retention_months || 12));
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const handleSaveMonths = async () => {
    const m = parseInt(draftMonths, 10);
    if (!m || m < 1) { toast("Enter a valid month count (≥ 1)", "error"); return; }
    setSavingMonths(true);
    try {
      await api.setHistoryRetention(m);
      toast(`History retention set to ${m} month${m === 1 ? "" : "s"}`);
      await reload();
    } catch (e) { toast(e.message, "error"); }
    setSavingMonths(false);
  };

  const handlePurge = async () => {
    const override = (purgeMonths || "").trim();
    let monthsArg;
    if (override) {
      monthsArg = parseInt(override, 10);
      if (!monthsArg || monthsArg < 1) {
        toast("Override must be a positive integer", "error");
        return;
      }
    }
    const cutoffLabel = override
      ? `older than ${monthsArg} month${monthsArg === 1 ? "" : "s"}`
      : `outside the ${data?.retention_months || 12}-month retention window`;
    if (!window.confirm(`Permanently delete all history rows ${cutoffLabel}? This cannot be undone.`)) return;
    setPurging(true);
    try {
      const res = await api.purgeHistory(monthsArg);
      toast(`Purged ${res.deleted} row${res.deleted === 1 ? "" : "s"}`);
      setPurgeMonths("");
      await reload();
    } catch (e) { toast(e.message, "error"); }
    setPurging(false);
  };

  return (
    <div className="card">
      <h3 className="heading fw-600 mb-8" style={{ fontSize: 16 }}>History Retention</h3>
      <p className="text-xs text-muted mb-16">
        Controls how far back the Change History views show. Older rows stay in the database
        (so audits remain available) until explicitly purged via the button below.
      </p>

      {loading && <div className="loading-container" style={{ minHeight: 60 }}><div className="spinner" /></div>}

      {!loading && data && (
        <>
          <div className="form-group"><label className="form-label">Retention window (months)</label>
            <div className="flex gap-8 items-center" style={{ flexWrap: "wrap" }}>
              <input className="input mono" type="number" min="1"
                value={draftMonths}
                disabled={!canEdit}
                onChange={(e) => setDraftMonths(e.target.value)}
                style={{ maxWidth: 120 }} />
              <button className="btn btn-primary btn-sm" onClick={handleSaveMonths}
                disabled={!canEdit || savingMonths
                  || parseInt(draftMonths, 10) === data.retention_months}>
                {savingMonths ? "Saving..." : "Save"}
              </button>
            </div>
            <p className="text-xxs text-dim mt-6">
              Cutoff: <span className="mono">{data.cutoff ? new Date(data.cutoff).toLocaleString() : "—"}</span>
            </p>
          </div>

          <div className="info-box mt-12 mb-16" style={{ padding: "10px 14px" }}>
            <div className="flex justify-between items-center" style={{ flexWrap: "wrap", gap: 8 }}>
              <span className="text-xs">
                <strong>{data.visible_rows.toLocaleString("en-IN")}</strong> visible row{data.visible_rows === 1 ? "" : "s"}
                <span className="text-dim" style={{ marginLeft: 8 }}>·</span>
                <span style={{ marginLeft: 8, color: "var(--orange-light)" }}>
                  {data.purgeable_rows.toLocaleString("en-IN")} row{data.purgeable_rows === 1 ? "" : "s"} eligible to purge
                </span>
              </span>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Purge old data</label>
            <div className="flex gap-8 items-center" style={{ flexWrap: "wrap" }}>
              <input className="input mono" type="number" min="1"
                placeholder="Override (months)"
                value={purgeMonths}
                disabled={!canEdit}
                onChange={(e) => setPurgeMonths(e.target.value)}
                style={{ maxWidth: 180 }} />
              <button className="btn btn-danger btn-sm" onClick={handlePurge}
                disabled={!canEdit || purging
                  || (purgeMonths === "" && data.purgeable_rows === 0)}>
                {purging ? "Purging..."
                  : purgeMonths
                    ? `Purge older than ${parseInt(purgeMonths, 10) || "?"} months`
                    : `Purge ${data.purgeable_rows.toLocaleString("en-IN")} old row${data.purgeable_rows === 1 ? "" : "s"}`}
              </button>
            </div>
            <p className="text-xxs text-dim mt-6">
              Leave the override blank to use the configured retention window. Action is permanent.
            </p>
          </div>
        </>
      )}
    </div>
  );
}


/* ─── Inline-editable Vertical card ─── */
function EditableVerticalCard({ vertical: v, canEdit, onSaved, toast }) {
  const [editingField, setEditingField] = useState(null);  // "name" | "code" | null
  const [draftName, setDraftName] = useState(v.name || "");
  const [draftCode, setDraftCode] = useState(v.code || "");

  const save = async (field) => {
    const newName = (draftName || "").trim();
    const newCode = (draftCode || "").trim();
    if (field === "name" && !newName) { toast("Name is required", "error"); return; }
    const payload = field === "name" ? { name: newName } : { code: newCode };
    if (field === "name" && newName === v.name) { setEditingField(null); return; }
    if (field === "code" && newCode === (v.code || "")) { setEditingField(null); return; }
    try {
      await api.updateVertical(v.id, payload);
      toast("Vertical updated");
      setEditingField(null);
      onSaved && onSaved();
    } catch (e) { toast(e.message, "error"); }
  };

  const onKey = (e, field) => {
    if (e.key === "Enter") { e.preventDefault(); save(field); }
    if (e.key === "Escape") {
      e.preventDefault();
      setDraftName(v.name || ""); setDraftCode(v.code || ""); setEditingField(null);
    }
  };

  return (
    <div className="sub-card flex items-center gap-10" style={{ minWidth: 240, padding: "10px 14px" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {editingField === "name" ? (
          <input className="input input-sm" autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={() => save("name")}
            onKeyDown={(e) => onKey(e, "name")}
            style={{ width: "100%" }} />
        ) : (
          <p className="text-sm fw-500"
            style={{ cursor: canEdit ? "text" : "default" }}
            onClick={() => canEdit && (setDraftName(v.name || ""), setEditingField("name"))}
            title={canEdit ? "Click to rename" : undefined}>
            {v.name}
          </p>
        )}
        {editingField === "code" ? (
          <input className="input input-sm mono mt-2" autoFocus
            value={draftCode}
            onChange={(e) => setDraftCode(e.target.value)}
            onBlur={() => save("code")}
            onKeyDown={(e) => onKey(e, "code")}
            placeholder="Code"
            style={{ width: "100%" }} />
        ) : (
          <p className="mono text-xxs text-accent mt-2"
            style={{ cursor: canEdit ? "text" : "default", minHeight: 14 }}
            onClick={() => canEdit && (setDraftCode(v.code || ""), setEditingField("code"))}
            title={canEdit ? "Click to edit code" : undefined}>
            {v.code || (canEdit ? <span className="text-dim" style={{ fontStyle: "italic" }}>+ code</span> : "—")}
          </p>
        )}
        <p className="text-xxs text-dim mt-2">{v.project_count || 0} project(s)</p>
      </div>
      {canEdit && (
        <button className="btn-icon" title="Delete vertical"
          onClick={async () => {
            try {
              await api.deleteVertical(v.id);
              toast(`Vertical "${v.name}" deleted`);
              onSaved && onSaved();
            } catch (err) { toast(err.message, "error"); }
          }}>
          <XIcon />
        </button>
      )}
    </div>
  );
}


/* ─── Inline-editable Company card ─── */
function EditableCompanyCard({ company: c, canEdit, onSaved, toast, onEditHierarchy }) {
  const [editingField, setEditingField] = useState(null);
  const [draftName, setDraftName] = useState(c.name || "");
  const [draftCode, setDraftCode] = useState(c.code || "");

  const save = async (field) => {
    const newName = (draftName || "").trim();
    const newCode = (draftCode || "").trim();
    if (field === "name" && !newName) { toast("Name is required", "error"); return; }
    const payload = field === "name" ? { name: newName } : { code: newCode };
    if (field === "name" && newName === c.name) { setEditingField(null); return; }
    if (field === "code" && newCode === (c.code || "")) { setEditingField(null); return; }
    try {
      await api.updateCompany(c.id, payload);
      toast("Company updated");
      setEditingField(null);
      onSaved && onSaved();
    } catch (e) { toast(e.message, "error"); }
  };

  const onKey = (e, field) => {
    if (e.key === "Enter") { e.preventDefault(); save(field); }
    if (e.key === "Escape") {
      e.preventDefault();
      setDraftName(c.name || ""); setDraftCode(c.code || ""); setEditingField(null);
    }
  };

  return (
    <div className="sub-card flex items-center gap-10" style={{ minWidth: 220, padding: "10px 14px" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {editingField === "name" ? (
          <input className="input input-sm" autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={() => save("name")}
            onKeyDown={(e) => onKey(e, "name")}
            style={{ width: "100%" }} />
        ) : (
          <p className="text-sm fw-500"
            style={{ cursor: canEdit ? "text" : "default" }}
            onClick={() => canEdit && (setDraftName(c.name || ""), setEditingField("name"))}
            title={canEdit ? "Click to rename" : undefined}>
            {c.name}
          </p>
        )}
        {editingField === "code" ? (
          <input className="input input-sm mono mt-4" autoFocus
            value={draftCode}
            onChange={(e) => setDraftCode(e.target.value)}
            onBlur={() => save("code")}
            onKeyDown={(e) => onKey(e, "code")}
            placeholder="Code"
            style={{ width: "100%" }} />
        ) : (
          <p className="mono text-xs text-accent mt-4"
            style={{ cursor: canEdit ? "text" : "default", minHeight: 14 }}
            onClick={() => canEdit && (setDraftCode(c.code || ""), setEditingField("code"))}
            title={canEdit ? "Click to edit code" : undefined}>
            {c.code || (canEdit ? <span className="text-dim" style={{ fontStyle: "italic" }}>+ code</span> : "—")}
          </p>
        )}
        {(c.project_count != null) && (
          <p className="text-xxs text-dim mt-2">{c.project_count || 0} project(s)</p>
        )}
      </div>
      {canEdit && (
        <>
          <button className="btn-icon" title="Edit hierarchy (Main / Parent / %, Vertical, Proposed)"
            onClick={() => onEditHierarchy && onEditHierarchy(c)}>
            <EditIcon />
          </button>
          <button className="btn-icon" title="Delete company (soft-delete; projects mapped here will lose their mapping)"
            onClick={async () => {
              const cnt = c.project_count || 0;
              const warn = cnt > 0
                ? `Delete company "${c.name}"? ${cnt} project(s) are mapped to it and will become Unmapped.`
                : `Delete company "${c.name}"?`;
              if (!window.confirm(warn)) return;
              try {
                await api.deleteCompany(c.id);
                toast(`Company "${c.name}" deleted`);
                onSaved && onSaved();
              } catch (err) { toast(err.message, "error"); }
            }}>
            <XIcon />
          </button>
        </>
      )}
    </div>
  );
}
